import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  useReveal, ServiceHero, StatsRow, ProcessSection, WhatWeDeliver,
  ServiceCaseStudies, ServiceFAQ,
  IndustriesSection, WhyZoneX, ServiceCTA, Footer,
} from './shared/ServicePageSections';

const COLOR = '#1A7A40';

const PROCESS = [
  {
    num:'01', title:'Problem Definition & Literature Review',
    desc:'Good research starts with a precisely framed question. We work with you to define the research problem clearly, review the existing technology landscape and academic literature, identify prior art and patent considerations, and set measurable success criteria. Without this, R&D becomes expensive wandering.',
    points:[
      'Research question framing and success criteria definition',
      'Technology landscape review and state-of-the-art analysis',
      'Patent search and prior art analysis for IP clarity',
      'Feasibility and risk assessment before any development',
    ],
  },
  {
    num:'02', title:'Technology Scouting & Feasibility',
    desc:'We identify and assess the most promising technologies, components, algorithms, and platforms for your research question. This involves hands-on evaluation — bench testing candidate sensors, running benchmark comparisons, and building small test rigs — alongside a rigorous trade-off analysis that gives you a clear, evidence-based path forward.',
    points:[
      'Technology option identification and candidate shortlisting',
      'Hands-on bench evaluation and benchmark testing',
      'Trade-off analysis across performance, cost, power, and risk dimensions',
      'Technology recommendation with documented evidence and rationale',
    ],
  },
  {
    num:'03', title:'Proof of Concept (PoC) Development',
    desc:'We build a focused PoC that tests the core technical hypothesis — not the whole product. A PoC should answer the most critical "can this work?" question as fast and cheaply as possible. We scope deliberately, avoiding scope creep, and define clear go/no-go criteria before we start building.',
    points:[
      'PoC scope definition with targeted hypothesis and go/no-go criteria',
      'Hardware, firmware, and/or algorithm PoC build',
      'Rapid iteration within fixed time-boxes to answer core questions',
      'Stage-gate review at defined milestones before continuing',
    ],
  },
  {
    num:'04', title:'Evaluation & Iteration',
    desc:'We measure the PoC results against the success criteria defined at the start, document the learnings rigorously, and make a clear recommendation: validate and proceed, pivot approach, or investigate further. Every result — positive or negative — is an asset. We don\'t hide failures; we extract their value.',
    points:[
      'Quantitative PoC evaluation against defined success criteria',
      'Learnings documentation — what worked, what failed, and why',
      'Pivot decision with alternative approach if needed',
      'Updated feasibility and risk assessment for next phase',
    ],
  },
  {
    num:'05', title:'Technology Transfer & Productisation Roadmap',
    desc:'R&D that stays in the lab has zero commercial value. We prepare everything a product team needs to continue development: full technology transfer documentation, IP summary, patentability insights, and a concrete productisation roadmap with effort and cost estimates. For institutional partners, we support co-authoring research papers.',
    points:[
      'Complete technology transfer documentation for internal product team',
      'IP documentation and patentability insights for novel innovations',
      'Productisation roadmap with effort, cost, and timeline estimates',
      'Academic paper co-authorship support for institutional R&D partners',
    ],
  },
];

const DELIVERABLES = [
  { title:'Technology Feasibility Study', desc:'Written trade-off analysis covering available technologies, components, algorithms, and platforms against your research requirements — with clear recommendation.' },
  { title:'Literature Review & Prior Art Summary', desc:'Structured review of existing solutions, academic research, and prior art relevant to your innovation area — with IP implications.' },
  { title:'Proof of Concept (PoC)', desc:'Working PoC demonstrating the core technical hypothesis — hardware, firmware, algorithm, or software as appropriate to your research question.' },
  { title:'PoC Evaluation Report', desc:'Quantitative evaluation of PoC results against defined success criteria, with documented learnings and clear next-phase recommendation.' },
  { title:'IP Documentation & Patentability Insights', desc:'Summary of patentability considerations and guidance on protecting innovations developed during the engagement.' },
  { title:'Technology Transfer Documentation', desc:'Rigorous documentation enabling internal product teams to continue development from where the R&D engagement ends — no knowledge silos.' },
  { title:'Productisation Roadmap', desc:'Detailed roadmap converting R&D output into a market-ready product — with effort estimates, cost projections, and milestone plan.' },
  { title:'Academic Paper Co-Authorship Support', desc:'For institutional and university partners, we support co-authoring research papers based on the work conducted together.' },
];

const CASE_STUDIES = [
  {
    tag: 'Edge AI',
    title: 'TinyML Anomaly Detection PoC',
    metric: '94% detection accuracy on MCU',
    desc: 'Feasibility study and PoC for vibration-based machine fault detection on a Cortex-M4 microcontroller — evaluated 3 algorithm approaches, deployed the winning model using TensorFlow Lite Micro, and documented a full productisation roadmap.',
  },
  {
    tag: 'Sensor R&D',
    title: 'Novel Soil NPK Sensing Approach',
    metric: 'Patent application filed',
    desc: 'Applied research into optical spectroscopy for low-cost soil nutrient measurement — systematic comparison of photodiode configurations, signal conditioning approaches, and ML-based calibration models.',
  },
  {
    tag: 'Wireless',
    title: 'Long-Range LoRa Study for Rural India',
    metric: '12km line-of-sight range achieved',
    desc: 'Technology feasibility study for a rural connectivity initiative — systematic field testing of LoRa parameters across terrain types common to Karnataka, with link budget modelling and gateway placement recommendations.',
  },
];

const FAQS = [
  {
    q: 'What is a Proof of Concept (PoC) and how is it different from a prototype?',
    a: 'A Proof of Concept tests a specific technical hypothesis — can this sensing approach work? Can this algorithm achieve the required accuracy? — using the simplest possible implementation. A prototype is a more complete implementation of the product concept. We recommend starting with a PoC when the core technical approach is unproven, because it answers critical unknowns cheaply before investing in a full prototype.',
  },
  {
    q: 'What research domains does Zone-X specialise in?',
    a: 'We conduct applied research across embedded systems, IoT hardware, sensor technology, wireless communication protocols, machine learning on edge devices (TinyML), and custom protocol development. We focus on research that can be converted into real products — not purely theoretical work.',
  },
  {
    q: 'Who owns the intellectual property developed during an R&D engagement?',
    a: 'IP ownership is agreed at the start of the engagement and is fully negotiable. For most commercial R&D engagements, all developed IP is owned by the client. We provide IP documentation, patentability insights, and technology transfer documentation to support any patent filing process.',
  },
  {
    q: 'Can academic institutions or universities work with Zone-X?',
    a: 'Yes. We work with engineering colleges, universities, and research institutions as an industry R&D partner — supporting student projects, funded research programs, and technology transfer initiatives. We also support academic paper co-authorship for research conducted jointly.',
  },
  {
    q: 'How do you define success criteria for an R&D project?',
    a: 'We define success criteria collaboratively at the start of every R&D engagement, before development begins. Success criteria are specific and measurable — a sensor achieving a defined accuracy at a defined cost, an algorithm running within a defined memory footprint, a communication protocol achieving a defined range. This prevents R&D from becoming open-ended.',
  },
];

const DOMAINS = [
  { title:'Embedded Systems Research', desc:'Novel sensing approaches, custom MCU architectures, real-time processing, and low-power design research for next-generation products.' },
  { title:'IoT Hardware Innovation', desc:'New form factors, ruggedized designs, multi-protocol communication, and miniaturization challenges for extreme environments.' },
  { title:'Sensor Technology', desc:'Custom sensor development, signal conditioning, multi-sensor fusion, and calibration research for high-precision applications.' },
  { title:'Wireless Communication', desc:'Custom protocol development, long-range RF design, mesh networking, and interference mitigation for challenging deployments.' },
  { title:'Edge AI / TinyML', desc:'Machine learning on resource-constrained microcontrollers — anomaly detection, classification, and on-device inference without the cloud.' },
  { title:'Custom Protocol Development', desc:'Proprietary communication protocols designed for specific range, power, and reliability requirements that commercial solutions can\'t meet.' },
];

function HeroVisual() {
  return (
    <img src="/visuals/services/Service 6.png" alt="Research, Innovation & Product R&D" style={{ width:'100%', display:'block' }} />
  );
}

export default function ResearchRnDPage() {
  const [heroRef, heroVisible] = useReveal();
  return (
    <>
      <Helmet>
        <title>IoT R&D Services India | Product Innovation — Zone-X</title>
        <meta name="description" content="Zone-X conducts applied IoT research and product R&D in India — proof of concept development, technology feasibility studies, TinyML, embedded systems research, and productisation roadmaps." />
        <link rel="canonical" href="https://zone-x.dev/research-innovation-rnd" />
      </Helmet>

      <div ref={heroRef}>
        <ServiceHero
          tag="Services / Research & Innovation"
          breadcrumb="Research & R&D"
          title="Research, Innovation &"
          titleAccent="Product R&D"
          subtitle="Where wild ideas meet rigorous engineering. R&D isn't a luxury — it's your competitive moat. Zone-X helps founders, enterprises, and institutions explore what's technically possible, prove it works with a focused proof of concept, and create a clear path to turning that possibility into a real product."
          geo="IoT R&D services for startups, enterprises, and institutions across India."
          color={COLOR}
          visible={heroVisible}
          visual={<HeroVisual />}
        />
      </div>

      <StatsRow />
      <RnDDomains color={COLOR} domains={DOMAINS} />
      <RnDMoat color={COLOR} />
      <ProcessSection steps={PROCESS} color={COLOR} />
      <WhatWeDeliver items={DELIVERABLES} color={COLOR} />
      <ServiceCaseStudies studies={CASE_STUDIES} color={COLOR} />
      <ServiceFAQ faqs={FAQS} color={COLOR} />
      <IndustriesSection />
      <WhyZoneX />
      <ServiceCTA />
      <Footer />
    </>
  );
}

function RnDDomains({ color, domains }) {
  const [ref, visible] = useReveal();
  return (
    <section style={{ padding:'0 0 clamp(32px,4vw,52px)' }}>
      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>
        <div ref={ref} style={{ marginBottom:32, opacity:visible?1:0, transform:visible?'none':'translateY(18px)', transition:'opacity 480ms, transform 480ms' }}>
          <span style={{ fontFamily:"'Anton', sans-serif", fontSize:10.5, color:color, textTransform:'uppercase' }}>RESEARCH DOMAINS</span>
          <h2 style={{ fontFamily:"'Anton',sans-serif", fontSize:'clamp(26px,3.5vw,42px)', lineHeight:1.1, marginTop:8, color:'var(--ink)' }}>
            Applied Research{' '}<span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color:color }}>Across</span>{' '}Embedded Innovation
          </h2>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14 }} className="rnd-three-col">
          {domains.map((d,i) => (
            <div key={d.title} style={{ padding:'clamp(18px,2.2vw,26px)', background:'rgba(255,255,255,0.78)', border:'1px solid rgba(10,10,20,0.08)', borderRadius:16, opacity:visible?1:0, transform:visible?'none':'translateY(18px)', transition:`opacity 450ms ${i*60+80}ms, transform 450ms ${i*60+80}ms` }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background:color, marginBottom:14 }}/>
              <div style={{ fontSize:14.5, fontWeight:700, color:'var(--ink)', marginBottom:8, lineHeight:1.3 }}>{d.title}</div>
              <div style={{ fontSize:13.5, lineHeight:1.7, color:'var(--ink-soft)' }}>{d.desc}</div>
            </div>
          ))}
        </div>
      </div>
      <style>{`@media(max-width:820px){.rnd-three-col{grid-template-columns:1fr 1fr!important}}@media(max-width:540px){.rnd-three-col{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
}

function RnDMoat({ color }) {
  const [ref, visible] = useReveal();
  return (
    <section style={{ padding:'0 0 clamp(40px,5vw,64px)' }}>
      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>
        <div ref={ref} style={{ background:`${color}07`, border:`1px solid ${color}1A`, borderRadius:24, padding:'clamp(28px,4vw,52px)', opacity:visible?1:0, transform:visible?'none':'translateY(22px)', transition:'opacity 520ms, transform 520ms' }}>
          <span style={{ fontFamily:"'Anton', sans-serif", fontSize:10.5, color:color, textTransform:'uppercase' }}>YOUR COMPETITIVE EDGE</span>
          <h2 style={{ fontFamily:"'Anton',sans-serif", fontSize:'clamp(22px,3vw,38px)', lineHeight:1.1, marginTop:8, marginBottom:16, color:'var(--ink)', maxWidth:600 }}>
            R&D isn't a luxury —{' '}
            <span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color:color }}>it's your competitive moat</span>
          </h2>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'clamp(20px,3vw,40px)' }} className="moat-two-col">
            <p style={{ fontSize:15, lineHeight:1.82, color:'var(--ink-soft)' }}>
              In a world where hardware products can be copied and software can be replicated, your R&D is often the only thing that gives you a durable edge. A novel sensing approach, a unique algorithm, a patentable design — these are the things competitors can't easily take. We thrive in ambiguity. If your idea is half-formed, if you're not sure the technology exists yet, or if you need to validate before you invest — this is exactly the conversation to be having with us.
            </p>
            <p style={{ fontSize:15, lineHeight:1.82, color:'var(--ink-soft)' }}>
              At Zone-X, we conduct applied research and product R&D across embedded systems, IoT hardware, sensor technology, wireless communication, machine learning on edge devices (TinyML), and custom protocol development. We work with startups building first-of-its-kind products, enterprises exploring new technology territory, and academic institutions looking for industry R&D partners.
            </p>
          </div>
        </div>
      </div>
      <style>{`@media(max-width:760px){.moat-two-col{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
}
